import axios from 'axios'
import { SESSION_KEY } from '../constants/storage'
import { hasValidSession, normalizeSessionPayload } from './session'

const timeout = Number(process.env.VUE_APP_API_TIMEOUT) || 15000

const requestConfig = {
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  timeout
}

function extractErrorMessage(payload) {
  if (!payload || typeof payload !== 'object') {
    return ''
  }

  return payload.msg || payload.message || payload.errorMsg || ''
}

function getResponseHeader(headers, name) {
  if (!headers) {
    return ''
  }

  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase()) || ''
  }

  return headers[name] || headers[name.toLowerCase()] || ''
}

function isSuccessPayload(payload) {
  return payload?.code === 200 || payload?.code === 0
}

function isPublicRequest(url = '') {
  return ['/user/login', '/user/register', '/captchaImage'].some((path) => url.includes(path))
}

function forceBackToLogin() {
  window.localStorage.removeItem(SESSION_KEY)
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

const request = axios.create(requestConfig)

request.interceptors.request.use(
  (config) => {
    if (isPublicRequest(config.url)) {
      return config
    }

    const rawSession = window.localStorage.getItem(SESSION_KEY)

    if (rawSession) {
      try {
        const session = normalizeSessionPayload(JSON.parse(rawSession))

        if (!hasValidSession(session)) {
          forceBackToLogin()
          return Promise.reject(new Error('登录状态已失效，请重新登录'))
        }

        config.headers.token = `Bearer ${session.token}`
        config.headers.Authorization = `Bearer ${session.token}`
      } catch (error) {
        forceBackToLogin()
        return Promise.reject(new Error('登录状态已失效，请重新登录'))
      }
    } else {
      forceBackToLogin()
      return Promise.reject(new Error('未检测到登录信息，请重新登录'))
    }

    return config
  },
  (error) => Promise.reject(error)
)

request.interceptors.response.use(
  (response) => {
    const payload = response.data
    const headerToken =
      getResponseHeader(response?.headers, 'token') ||
      getResponseHeader(response?.headers, 'authorization') ||
      ''

    if (payload && typeof payload === 'object' && headerToken && !payload.token) {
      payload.token = String(headerToken).replace(/^Bearer\s+/i, '')
    }

    if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'code')) {
      if (!isSuccessPayload(payload)) {
        return Promise.reject(new Error(extractErrorMessage(payload) || '请求失败'))
      }

      return payload
    }

    return payload
  },
  (error) => {
    if (error?.response?.status === 401) {
      forceBackToLogin()
    }

    const message =
      extractErrorMessage(error?.response?.data) ||
      error?.message ||
      '请求失败'

    return Promise.reject(new Error(message))
  }
)

export default request
